import { useState, type ReactElement } from 'react';
import {
  api,
  DELIVERY_MODES,
  type DeliveryMode,
  type ParameterView,
} from '../shared/api/endpoints';
import { useMessages } from '../shared/i18n/locale';
import { codeLabel } from '../shared/lib/status';
import { formatDateTime } from '../shared/lib/format';
import { useAsync } from '../shared/lib/use-async';
import {
  Card,
  EmptyState,
  Field,
  primaryButtonStyle,
  secondaryButtonStyle,
} from '../shared/ui/primitives';
import { EmbedDomainsCard } from './EmbedDomainsCard';

/**
 * Эксплуатация: интеграции, флаги, параметры, SLO (разд. 13).
 *
 * Раздел администратора. Числа здесь не для отчёта, а для работы: непустая DLQ
 * означает, что CT остался со старой картиной по конкретным заказам, и разбирается
 * это кнопкой повторной отправки, а не наблюдением.
 */

export function IntegrationsPage({
  canEdit = false,
}: {
  /** Правка доменов встраивания — у администратора (FR-635). */
  readonly canEdit?: boolean | undefined;
}): ReactElement {
  const messages = useMessages();
  const integrations = useAsync(() => api.integrations(), [], { intervalMs: REFRESH_MS });

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
      <h1>{messages.cabinet.ops.integrations}</h1>

      {integrations.error === null ? null : (
        <div style={{ color: 'var(--danger)' }}>
          {integrations.error.status === FORBIDDEN
            ? messages.cabinet.common.forbidden
            : `${messages.cabinet.common.error}: ${integrations.error.code}`}
        </div>
      )}

      {(integrations.data ?? []).map((integration) => (
        <Card
          key={integration.integration}
          tone={
            integration.state === 'failed'
              ? 'danger'
              : integration.state === 'degraded'
                ? 'warn'
                : 'ok'
          }
        >
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <h2>
              {codeLabel(messages.cabinet.ops.integrationNames, integration.integration ?? '')}
            </h2>
            <span>{codeLabel(messages.cabinet.ops.states, integration.state ?? '')}</span>
          </div>

          <div
            style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--sp-3)' }}
          >
            <Field
              label={messages.cabinet.ops.lastSuccess}
              value={<span className="num">{formatDateTime(integration.last_success_at)}</span>}
            />
            <Field
              label={messages.cabinet.ops.errors24h}
              value={<span className="num">{integration.errors_24h ?? 0}</span>}
            />
            <Field
              label={messages.cabinet.ops.queue}
              value={<span className="num">{integration.queue_size ?? 0}</span>}
            />
            <Field
              label={messages.cabinet.ops.dlq}
              value={
                <span
                  className="num"
                  style={{ color: (integration.dlq_size ?? 0) > 0 ? 'var(--danger)' : undefined }}
                >
                  {integration.dlq_size ?? 0}
                </span>
              }
            />
          </div>
        </Card>
      ))}

      {(integrations.data ?? []).length === 0 ? (
        <EmptyState text={messages.cabinet.common.empty} />
      ) : null}

      {/* Встраивание представлений — тоже интеграция с CT: рядом с её состоянием. */}
      <EmbedDomainsCard canEdit={canEdit} />
    </div>
  );
}

export function FlagsPage({ canEdit }: { readonly canEdit: boolean }): ReactElement {
  const messages = useMessages();
  const flags = useAsync(() => api.flags(), []);
  const [error, setError] = useState<string | null>(null);

  const toggle = async (code: string, enabled: boolean): Promise<void> => {
    setError(null);

    try {
      await api.setFlag(code, enabled);
      flags.reload();
    } catch (cause) {
      setError(String(cause));
    }
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
      <h1>{messages.cabinet.ops.flags}</h1>

      {error === null ? null : <div style={{ color: 'var(--danger)' }}>{error}</div>}

      <Card>
        {(flags.data ?? []).map((flag) => (
          <div
            key={flag.code}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            {/* Код флага показан мелко рядом: подписан смысл, а не идентификатор. */}
            <span className="num" style={{ color: 'var(--text-muted)' }}>
              {flag.code}
            </span>
            <input
              type="checkbox"
              disabled={!canEdit}
              checked={flag.is_enabled === true}
              onChange={(event) => void toggle(flag.code ?? '', event.target.checked)}
              aria-label={flag.code}
            />
          </div>
        ))}
      </Card>
    </div>
  );
}

export function ParametersPage({ canEdit }: { readonly canEdit: boolean }): ReactElement {
  const messages = useMessages();
  const parameters = useAsync(() => api.parameters(), []);
  const [error, setError] = useState<string | null>(null);
  const [edited, setEdited] = useState<Record<string, string>>({});

  /** `mode` — значение отдела (ADR-0030); ключ правки — `ключ@режим`. */
  const save = async (key: string, mode?: DeliveryMode): Promise<void> => {
    setError(null);
    const raw = edited[mode === undefined ? key : `${key}@${mode}`] ?? '';

    try {
      // Значение параметра бывает числом, флагом и строкой перечисления: тип знает
      // реестр П-И, и он же отклонит значение вне диапазона (FR-417).
      const parsed =
        raw === 'true'
          ? true
          : raw === 'false'
            ? false
            : Number.isNaN(Number(raw))
              ? raw
              : Number(raw);
      await api.setParameter(key, parsed, mode);
      parameters.reload();
    } catch (cause) {
      setError(String(cause));
    }
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
      <h1>{messages.cabinet.ops.parameters}</h1>

      {error === null ? null : <div style={{ color: 'var(--danger)' }}>{error}</div>}

      <Card>
        <table>
          <tbody>
            {(parameters.data ?? []).map((parameter) => (
              <tr key={parameter.key}>
                <td style={cellStyle}>
                  <div style={{ display: 'grid', gap: '2px' }}>
                    <span className="num">{parameter.key}</span>
                    <span style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-caption)' }}>
                      {parameter.purpose}
                    </span>
                    {parameter.by_mode === undefined ? null : (
                      <ModeValues
                        parameterKey={parameter.key}
                        byMode={parameter.by_mode}
                        canEdit={canEdit}
                        onEdit={(editKey, value) =>
                          setEdited((current) => ({ ...current, [editKey]: value }))
                        }
                        onSave={(mode) => void save(parameter.key, mode)}
                        isEdited={(editKey) => edited[editKey] !== undefined}
                      />
                    )}
                  </div>
                </td>
                <td style={{ ...cellStyle, width: '160px' }}>
                  <input
                    disabled={!canEdit}
                    defaultValue={String(parameter.value ?? '')}
                    onChange={(event) =>
                      setEdited((current) => ({ ...current, [parameter.key]: event.target.value }))
                    }
                  />
                </td>
                <td style={{ ...cellStyle, width: '120px', color: 'var(--text-faint)' }}>
                  <span className="num">
                    {parameter.min === null || parameter.min === undefined
                      ? ''
                      : `${parameter.min}–${parameter.max ?? ''}`}
                  </span>
                </td>
                <td style={{ ...cellStyle, width: '120px' }}>
                  {canEdit ? (
                    <button
                      type="button"
                      style={secondaryButtonStyle}
                      disabled={edited[parameter.key] === undefined}
                      onClick={() => void save(parameter.key)}
                    >
                      {messages.cabinet.common.apply}
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/**
 * Значения отделов под общим (ADR-0030). Показываются только заданные отдельно:
 * у отдела без своего значения действует общее, и дублировать его строкой — значит
 * заставить читать одно и то же дважды.
 */
function ModeValues({
  parameterKey,
  byMode,
  canEdit,
  onEdit,
  onSave,
  isEdited,
}: {
  readonly parameterKey: string;
  readonly byMode: NonNullable<ParameterView['by_mode']>;
  readonly canEdit: boolean;
  readonly onEdit: (editKey: string, value: string) => void;
  readonly onSave: (mode: DeliveryMode) => void;
  readonly isEdited: (editKey: string) => boolean;
}): ReactElement | null {
  const messages = useMessages();
  const modes = DELIVERY_MODES.filter((mode) => byMode[mode] !== undefined);

  if (modes.length === 0) {
    return null;
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-1)', marginTop: 'var(--sp-1)' }}>
      <span style={{ color: 'var(--text-faint)', fontSize: 'var(--fs-caption)' }}>
        {messages.cabinet.ops.byMode}
      </span>
      {modes.map((mode) => {
        const editKey = `${parameterKey}@${mode}`;

        return (
          <div key={mode} style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
            <span style={{ minWidth: '160px' }}>{messages.cabinet.modes[mode]}</span>
            <input
              style={{ width: '120px' }}
              disabled={!canEdit}
              defaultValue={String(byMode[mode]?.value ?? '')}
              onChange={(event) => onEdit(editKey, event.target.value)}
            />
            {canEdit ? (
              <button
                type="button"
                style={secondaryButtonStyle}
                disabled={!isEdited(editKey)}
                onClick={() => onSave(mode)}
              >
                {messages.cabinet.common.apply}
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function SloPage(): ReactElement {
  const messages = useMessages();
  const slo = useAsync(() => api.slo(), []);

  // Нарушенные показатели — сверху: остальные диспетчер и администратор смотрят
  // раз в неделю, а эти требуют действия сегодня.
  const indicators = [...(slo.data ?? [])].sort(
    (left, right) => Number(right.breached) - Number(left.breached),
  );

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
      <h1>{messages.cabinet.ops.slo}</h1>

      {indicators.length === 0 ? <EmptyState text={messages.cabinet.common.empty} /> : null}

      {indicators.map((indicator) => (
        <Card key={indicator.code} tone={indicator.breached === true ? 'danger' : 'ok'}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
            <span>
              {messages.cabinet.ops.sloNames[
                indicator.code as keyof typeof messages.cabinet.ops.sloNames
              ] ?? indicator.name}
            </span>
            <span>
              <span className="num">{indicator.current ?? 0}</span>
              {' / '}
              <span className="num" style={{ color: 'var(--text-muted)' }}>
                {indicator.target ?? 0}
              </span>
            </span>
          </div>
        </Card>
      ))}
    </div>
  );
}

export function OpsActions({ onRetry }: { readonly onRetry: () => void }): ReactElement {
  const messages = useMessages();

  return (
    <button type="button" style={primaryButtonStyle} onClick={onRetry}>
      {messages.cabinet.ops.retry}
    </button>
  );
}

const REFRESH_MS = 30_000;
const FORBIDDEN = 403;

const cellStyle = {
  padding: 'var(--sp-2) var(--sp-3)',
  borderBottom: '1px solid var(--border)',
  verticalAlign: 'top' as const,
};
